/**
 * Editing the catalog: her pots (with the asset register), clients, spots and requirements.
 *
 * The catalog is data she owns and changes; history refers to it by id. So edits follow one rule:
 * nothing that history depends on can disappear or change identity.
 * - A pot's number never changes (it's printed on the sticker) and pots are never deleted;
 *   they retire (out of circulation) or are written off (a recorded event).
 * - A spot or client with placements on record can't be removed; it can be renamed.
 * Pure: each edit returns new data or an error message she can read.
 */
import type { CatalogData, Client, Pot, Requirement, Slot } from "./catalog";
import type { History } from "./history";

export type EditResult = { readonly ok: true; readonly data: CatalogData } | { readonly ok: false; readonly error: string };

const ok = (data: CatalogData): EditResult => ({ ok: true, data });
const fail = (error: string): EditResult => ({ ok: false, error });

/** Pot numbers as printed on stickers: one or two letters, then one to three digits. */
export const POT_NUMBER = /^[A-Z]{1,2}\d{1,3}$/;

export function addPot(data: CatalogData, pot: Pot): EditResult {
  const id = pot.id.trim().toUpperCase();
  if (!POT_NUMBER.test(id)) return fail("Use letters then numbers, like A1 or B12.");
  if (data.pots.some((p) => p.id === id)) return fail(`${id} already exists.`);
  return ok({ ...data, pots: [...data.pots, { ...pot, id }] });
}

/** Change a pot's description, asset details or circulation; its number stays. */
export function updatePot(data: CatalogData, pot: Pot): EditResult {
  if (!data.pots.some((p) => p.id === pot.id)) return fail(`${pot.id} isn't in your pot list.`);
  if (pot.asset?.costCents !== undefined && (!Number.isInteger(pot.asset.costCents) || pot.asset.costCents < 0)) {
    return fail("The cost must be a positive amount.");
  }
  return ok({ ...data, pots: data.pots.map((p) => (p.id === pot.id ? pot : p)) });
}

export function addClient(data: CatalogData, client: Client): EditResult {
  const name = client.name.trim();
  if (!name) return fail("A client needs a name.");
  if (data.clients.some((c) => c.id === client.id)) return fail("That client already exists.");
  if (data.clients.some((c) => c.name.toLowerCase() === name.toLowerCase())) return fail(`You already have a client called ${name}.`);
  return ok({ ...data, clients: [...data.clients, { ...client, name }] });
}

export function updateClient(data: CatalogData, client: Client): EditResult {
  const name = client.name.trim();
  if (!name) return fail("A client needs a name.");
  if (!data.clients.some((c) => c.id === client.id)) return fail("That client no longer exists.");
  if (data.clients.some((c) => c.id !== client.id && c.name.toLowerCase() === name.toLowerCase())) return fail(`You already have a client called ${name}.`);
  return ok({ ...data, clients: data.clients.map((c) => (c.id === client.id ? { ...client, name } : c)) });
}

/** Only a client with no spots can be removed (spots carry the history). */
export function removeClient(data: CatalogData, clientId: string): EditResult {
  if (data.slots.some((s) => s.clientId === clientId)) return fail("Remove this client's spots first.");
  return ok({ ...data, clients: data.clients.filter((c) => c.id !== clientId) });
}

export function addSlot(data: CatalogData, slot: Slot): EditResult {
  const label = slot.label.trim();
  if (!label) return fail("A spot needs a name, like Reception.");
  if (!data.clients.some((c) => c.id === slot.clientId)) return fail("That client no longer exists.");
  if (data.slots.some((s) => s.id === slot.id)) return fail("That spot already exists.");
  if (data.slots.some((s) => s.clientId === slot.clientId && s.label.toLowerCase() === label.toLowerCase())) return fail(`This client already has a spot called ${label}.`);
  return ok({ ...data, slots: [...data.slots, { ...slot, label }] });
}

export function updateSlot(data: CatalogData, slot: Slot): EditResult {
  const label = slot.label.trim();
  if (!label) return fail("A spot needs a name, like Reception.");
  const existing = data.slots.find((s) => s.id === slot.id);
  if (!existing) return fail("That spot no longer exists.");
  if (existing.clientId !== slot.clientId) return fail("A spot can't move to another client.");
  if (data.slots.some((s) => s.id !== slot.id && s.clientId === slot.clientId && s.label.toLowerCase() === label.toLowerCase())) return fail(`This client already has a spot called ${label}.`);
  return ok({ ...data, slots: data.slots.map((s) => (s.id === slot.id ? { ...slot, label } : s)) });
}

/** A spot with placements on record keeps its history: it can be renamed, not removed. */
export function removeSlot(data: CatalogData, slotId: string, history: History): EditResult {
  if (history.placements.some((p) => p.slotId === slotId)) return fail("This spot has history, so it stays. You can rename it.");
  return ok({ ...data, slots: data.slots.filter((s) => s.id !== slotId) });
}

/** A requirement is valid when it names an attribute and at least one value. */
export function validRequirement(r: Requirement): string | null {
  if (!r.attribute.trim()) return "Choose what the requirement is about (colour or size).";
  if (r.values.filter((v) => v.trim()).length === 0) return "Choose at least one value.";
  return null;
}
